import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TeacherCreateclassroomComponent } from '../teacher-createclassroom/teacher-createclassroom.component';
import { MatDialog } from '@angular/material/dialog';
import { NgFor } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { Router } from '@angular/router';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { ClassroomPostDialogBoxComponent } from '../classroom/classroom-post-dialog-box/classroom-post-dialog-box.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { TeacherServicesService } from '@edtech-ui/core/services/teacher-services.service';
import { DashboardCardDataInterface} from '@edtech-ui/core/models/dashboard/users-card-data';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { TeachersNotificationsComponent } from '../inbox/teachers-notifications/teachers-notifications.component';
import { UsagePlanService } from '@edtech-ui/core/services/usage-management/usage-plan.service';
import { UserPlanQuotaResponse } from '@edtech-ui/core/models/free-plan-usages/get-free-plan-usages';

@Component({
	selector: 'app-teacher-dashboard-content',
	standalone: true,
	imports: [NgFor, SharedModule, MatTooltipModule, MatProgressSpinnerModule,IstDatePipe ,TeachersNotificationsComponent,
	],
	templateUrl: './teacher-dashboard-content.component.html',
	styleUrl: './teacher-dashboard-content.component.scss'
})
export class TeacherDashboardContentComponent implements OnInit, OnDestroy {

	searchQuery = '';
	userDetail: UserDetailResponse['Data'] | null = null;
	userId = '';
	subscription = new Subscription();
	cardData: DashboardCardDataInterface[] = [];
	isLoading: boolean = false;
	isNotificationOpen = false;
	totalNotifications: number = 0;
	@ViewChild(TeachersNotificationsComponent) notificationsChild!: TeachersNotificationsComponent;
	usagePlaneData : UserPlanQuotaResponse['Data'] | null= null;

	constructor(
		private dialog: MatDialog,
		private router: Router,
		private _authService: AuthServiceService,
		private _teacherService: TeacherServicesService,
		private _toastr: ToastrService,
		private _usagePlanService:UsagePlanService,
	) { }

	ngOnInit(): void {
		const storedId = localStorage.getItem('user_id');
		if (storedId) {
			this.userId = storedId;
			this.getUserInfo(this.userId);
		}
		this.getUserDashboardInfo();
		this.getUsagePlan();
	}

	openCreateClassDialog() {
		this.dialog.open(TeacherCreateclassroomComponent, {
			data: { mode: 'create' }
		});
	}

	getUserDashboardInfo(): void {
		this.isLoading = true;
		this.subscription.add(
			this._teacherService.getDashboardInfo().pipe(
				tap(response => {
					if (response.Success && response.Data) {
						this.isLoading = false;
						const _userDashboardInfo = response.Data;

						this.cardData = [
							{
								studentCount: _userDashboardInfo.active_classrooms,
								title: 'Active Classrooms',
								image: 'assets/images/card1.png'
							},
							{
								studentCount: _userDashboardInfo.total_students_enrolled,
								title: 'Total Students Enrolled',
								image: 'assets/images/card2.png'
							},
							{
								studentCount: _userDashboardInfo.total_quizzes_created,
								title: 'Total Quizzes Created',
								image: 'assets/images/card4.png'
							},
							{
								studentCount: _userDashboardInfo.total_courses_created,
								title: 'Total Courses Created',
								image: 'assets/images/card3.png'
							}
						];
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching the information');
					return of(null);
				}
				)).subscribe()
		);
	}

	onViewClick(title: string) {
		switch (title) {
			case 'Active Classrooms':
				this.router.navigate(['/teacher-dashboard/classroom']);
				break;

			case 'Total Students Enrolled':
				this.router.navigate(['/teacher-dashboard/allstudents']);
				break;

			case 'Total Quizzes Created':
				this.router.navigate(['/teacher-dashboard/quizzes']);
				break;

			case 'Total Courses Created':
				this.router.navigate(['/teacher-dashboard/courses']);
				break;

			default:
				this._toastr.error("Navigation path not defined for this card");
		}
	}


	getUserInfo(id: string) {
		this.subscription.add(
			this._authService.getUserDetails(id).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success) {
						this.userDetail = response.Data;
						localStorage.setItem('is_tenant_member',JSON.stringify(response.Data.is_tenant_member));
					}
				},
			error: () => this._toastr.error('Something went wrong while fecthing the user Details')
			})
		);
	}

	goToClassroomPage() {
		this.router.navigate(['teacher-dashboard/classroom']);
	}

	openJoinDialogBox() {
		this.dialog.open(ClassroomPostDialogBoxComponent, {
			data: { userRole: Roles.TEACHER }
		});
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	updateTotalNotifications(count: number) {
		this.totalNotifications = count;
	}
	
	markAllNotificationsRead() {
		if (this.notificationsChild) {
			this.notificationsChild.markAllNotificationsAsRead();
		}
	}

	getUsagePlan(): void {
		this.subscription.add(
			this._usagePlanService.getUsagePlan().subscribe({
				next: (response: UserPlanQuotaResponse) => {
					if (response.Success) {
						this.usagePlaneData = response.Data;
					}
				},
				error: () => this._toastr.error('Something went wrong while fetching the usage plans')
			})
		);
	}

	logout() {
		const currentUserId = localStorage.getItem('user_id');
		this._authService.logout().pipe(
			tap((response) => {
				if (response.Success) {
					this._toastr.success('Logged out successfully.');
					this.router.navigate(['/login']);
				} else {
					this._toastr.error(response.Message || 'Logout failed');
				}
			}),
			finalize(() => {
				if (currentUserId) {
					localStorage.setItem('last_active_user_id', currentUserId);
					localStorage.removeItem(`${currentUserId}_ai_quiz_data`);
				} else {
					localStorage.removeItem('ai_quiz_data');
				}
				['token', 'user_id', 'role_id', 'email', 'ai_quiz_data', 'is_tenant_onboard', 'role_mode','company_id','last_active_user_id',
			    'user_time_zone', 'tenant_id', 'onesignal_player_id', 'modules', 'fcmToken', 'country','job_id','phone_email','fcmTokenTimestamp'
				].forEach(key => localStorage.removeItem(key));
			})
		).subscribe({
			error: () => {
				this._toastr.error('An error occurred during logout');
			}
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}